import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFormType } from '@neo/blocks/forms/types'
import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import { Input } from '@neo/primitives/input'
import { Box, Braces, Code, Minus, Plus } from 'lucide-react'
import { getMcpToolsByOrgAppUuid } from 'v2/pages/flow/utils/ai-action-utils'
import { useExternalOrgAppsQuery } from 'v2/pages/settings/integrations/queries'
import z from 'zod'
import { useGenerateFunctionSchemaMutation, useGetMcpToolsQuery } from '../../../queries'
import type { AiActionNodeData } from '../../../types'
import { useActionFormConfig } from './action-form-v2.config'

const getActionIcon = (actionType: string) => {
  if (actionType === 'EXTRACT_VARIABLES') return <Braces className="size-5" />
  if (actionType === 'EXECUTE_ACTION') return <Box className="size-5" />
  if (actionType === 'CUSTOM_CODE') return <Code className="size-5" />
  return null
}

export const MultipleActionsForm = ({
  form,
  nodeVars,
  arrayIndex,
}: {
  form: GenericFormType<AiActionNodeData['config']>
  nodeVars: string[]
  arrayIndex: number
}) => {
  const { data: activeApps, isLoading: isLoadingApps } = useExternalOrgAppsQuery(true)
  const { data: mcpTools, isLoading: isLoadingMcpTools } = useGetMcpToolsQuery()
  const { mutate: generateFunctionSchema, isPending: isGeneratingFunctionSchema } =
    useGenerateFunctionSchemaMutation()

  const { fields } = useActionFormConfig({
    form,
    nodeVars,
    arrayIndex,
    activeApps,
    isLoadingApps,
    mcpTools,
    isLoadingMcpTools,
    generateFunctionSchema,
    isGeneratingFunctionSchema,
    getMcpToolsByOrgAppUuid,
  })

  return (
    <div className="relative rounded-lg border p-4 flex flex-col gap-4 has-[.field-error-message]:border-destructive">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between relative w-full pr-12">
          <form.Field
            name={`actions[${arrayIndex}].name`}
            validators={{
              onChange: z.string().optional().nullable(),
            }}
            children={(field) => (
              <div className="w-full">
                <div className="flex items-center gap-2">
                  {getActionIcon(form.state.values.actions[arrayIndex].action_type || '')}
                  <div className="flex items-center gap-2 w-full">
                    <Input
                      className="text-p-500 text-secondary w-full p-0 h-5 shadow-none placeholder:text-tertiary"
                      inputClassName={cn(
                        'border-0 focus:border-b focus:border-solid focus:border-primary truncate focus:-top-1 relative transition-top duration-700',
                        !form.state.values.actions[arrayIndex].name && 'focus:border-destructive',
                      )}
                      value={field.state.value ?? ''}
                      variant="ghost"
                      autoFocus={false}
                      onChange={(e) => {
                        field.handleChange(e.target.value)
                      }}
                      placeholder="Enter action name"
                    />
                  </div>
                </div>
                {!form.state.values.actions[arrayIndex].is_expanded && (
                  <div className="flex flex-col gap-3">
                    <div className="text-xs-400 text-tertiary pl-7 truncate">
                      {form.state.values.actions[arrayIndex].code_description ||
                        form.state.values.actions[arrayIndex].variable_instructions ||
                        form.state.values.actions[arrayIndex].action_instructions}
                    </div>
                  </div>
                )}
              </div>
            )}
          />
          <form.Field
            name={`actions[${arrayIndex}].is_expanded`}
            children={(field) => (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                className="p-0 hover:bg-transparent absolute right-0 -top-2"
                onClick={() => field.handleChange(!field.state.value)}
                data-testid="button-toggle-action"
                aria-expanded={field.state.value}
                aria-label={field.state.value ? 'Collapse action' : 'Expand action'}
              >
                {field.state.value ? (
                  <Minus className="h-4 w-4 text-secondary" />
                ) : (
                  <Plus className="h-4 w-4 text-secondary" />
                )}
              </Button>
            )}
          />
        </div>
      </div>
      <div
        className={cn(
          'flex flex-col gap-4',
          !form.state.values.actions[arrayIndex].is_expanded && 'hidden',
        )}
      >
        <FieldsRenderer
          form={form}
          fields={fields}
          baseName={`actions[${arrayIndex}]`}
          arrayIndex={arrayIndex}
        />
      </div>
    </div>
  )
}
